type SelectableElement = HTMLInputElement | HTMLTextAreaElement;

const copyWithExecCommand = (text: string, element?: SelectableElement | null): boolean => {
  // use the given (visible) element if possible, so the selection stays inside open dialogs (focus trap)
  // and remains selected for a manual copy if the browser blocks the command
  const target = element ?? document.createElement('textarea');
  const isTemporary = !element;

  if (isTemporary) {
    target.value = text;
    target.setAttribute('readonly', '');
    target.style.position = 'fixed';
    target.style.top = '0';
    target.style.left = '-9999px';
    document.body.appendChild(target);
  }

  try {
    target.focus();
    target.select();
    target.setSelectionRange(0, target.value.length);

    // execCommand does not throw when the copy is blocked, it returns false
    return document.execCommand('copy');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Copy to clipboard via execCommand failed:', err);
    return false;
  } finally {
    if (isTemporary) {
      document.body.removeChild(target);
    }
  }
};

/**
 * Copies the text to the clipboard. Returns false if the browser refused the copy.
 * Note: some managed browser profiles (DLP policies) silently drop clipboard writes, which cannot be detected,
 * so the text should always be displayed for a manual copy as well.
 * Has to be called directly from a user gesture (click handler).
 */
export const copyToClipboard = async (
  text: string,
  fallbackElement?: SelectableElement | null
): Promise<boolean> => {
  if (window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Clipboard API write failed:', (err as Error)?.name, err);
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn('Clipboard API not available, secure context:', window.isSecureContext);
  }

  return copyWithExecCommand(text, fallbackElement);
};
